import { useState, useEffect } from 'react';
import { View, Modal, Alert, KeyboardAvoidingView, Platform, ScrollView, Pressable, Keyboard } from 'react-native';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SubHeading, ThemedText } from '@/components/ui/Text';
import type { TrustedContact } from '@/services/safety';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type ContactFormProps = {
  visible: boolean;
  contact?: TrustedContact | null;
  onClose: () => void;
  onSave: (contact: Omit<TrustedContact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
};

export function ContactForm({ visible, contact, onClose, onSave }: ContactFormProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact) {
      setName(contact.name);
      setPhoneNumber(contact.phoneNumber);
      setEmail(contact.email || '');
      setRelationship(contact.relationship || '');
      setIsPrimary(contact.isPrimary || false);
    } else {
      setName('');
      setPhoneNumber('');
      setEmail('');
      setRelationship('');
      setIsPrimary(false);
    }
  }, [contact, visible]);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) {
      newErrors.name = 'Please enter a name';
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Please enter a phone number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim() || undefined,
        relationship: relationship.trim() || undefined,
        isPrimary,
      });
      Alert.alert('Saved', 'Trusted contact saved.');
      onClose();
    } catch (error) {
      Alert.alert('Unable to save', 'We couldn’t save this contact right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      accessibilityViewIsModal={true}
      accessibilityLabel={contact ? 'Edit contact' : 'Add trusted contact'}
    >
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        behavior={Platform.OS === 'ios' ? 'height' : undefined}
        keyboardVerticalOffset={0}
      >
        <View
          accessibilityLabel={contact ? 'Edit contact form' : 'Add trusted contact form'}
          style={{ flex: 1, backgroundColor: theme.colors.background }}
        >
          <ScrollView
            style={{ flex: 1, backgroundColor: theme.colors.background }}
            contentContainerStyle={{
              flexGrow: 1,
              padding: theme.spacing(2),
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
            {/* Tap outside inputs to dismiss keyboard */}
            <Pressable onPress={() => Keyboard.dismiss()} style={{ flexGrow: 1 }} accessible={false}>
              <SubHeading style={{ marginBottom: theme.spacing(2) }}>
                {contact ? 'Edit Contact' : 'Add Trusted Contact'}
              </SubHeading>

              <View style={{ gap: theme.spacing(2), flex: 1 }}>
                {/* Error Summary */}
                {Object.keys(errors || {}).length > 0 && (
                  <View
                    accessibilityRole="alert"
                    accessibilityLiveRegion="assertive"
                    style={{
                      padding: theme.spacing(1.5),
                      backgroundColor: theme.colors.error + '15',
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: theme.colors.error + '40',
                      gap: theme.spacing(0.5),
                    }}
                  >
                    <ThemedText variant="bodyStrong" color={theme.colors.error}>
                      Please fix the following errors:
                    </ThemedText>
                    {Object.entries(errors || {}).map(([field, message]) => (
                      <ThemedText key={field} variant="body" color={theme.colors.error}>
                        • {message}
                      </ThemedText>
                    ))}
                  </View>
                )}

                <InputField
                  label="Name"
                  required
                  value={name}
                  onChangeText={(text) => {
                    setName(text);
                    if (errors.name) {
                      setErrors((prev) => ({ ...prev, name: '' }));
                    }
                  }}
                  placeholder="Enter contact name"
                  autoCapitalize="words"
                  errorText={errors.name}
                />

                <InputField
                  label="Phone Number"
                  required
                  value={phoneNumber}
                  onChangeText={(text) => {
                    setPhoneNumber(text);
                    if (errors.phoneNumber) {
                      setErrors((prev) => ({ ...prev, phoneNumber: '' }));
                    }
                  }}
                  placeholder="+1 234 567 8900"
                  keyboardType="phone-pad"
                  errorText={errors.phoneNumber}
                />

                <InputField
                  label="Email (optional)"
                  value={email}
                  onChangeText={setEmail}
                  placeholder="email@example.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />

                <InputField
                  label="Relationship (optional)"
                  value={relationship}
                  onChangeText={setRelationship}
                  placeholder="e.g., Emergency Contact, Family, Friend"
                  autoCapitalize="words"
                />

                <View style={{ marginTop: theme.spacing(1) }}>
                  <Button
                    label={isPrimary ? 'Primary Contact' : 'Set as Primary Contact'}
                    variant={isPrimary ? 'primary' : 'secondary'}
                    onPress={() => setIsPrimary(!isPrimary)}
                    accessibilityLabel={isPrimary ? 'Primary contact, tap to unset' : 'Set as primary contact'}
                    accessibilityHint="Marks this contact as the primary emergency contact"
                  />
                </View>
              </View>
            </Pressable>
          </ScrollView>

          {/* Footer actions stay reachable above the keyboard */}
          <View
            style={{
              padding: theme.spacing(2),
              gap: theme.spacing(1.5),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
              backgroundColor: theme.colors.background,
            }}
          >
            <Button
              label={saving ? 'Saving...' : 'Save'}
              onPress={handleSave}
              disabled={saving}
              accessibilityLabel={saving ? 'Saving contact, please wait' : 'Save contact'}
              accessibilityState={{ disabled: saving, busy: saving }}
            />
            <Button
              label="Cancel"
              variant="ghost"
              onPress={onClose}
              accessibilityLabel="Cancel and close form"
              accessibilityHint="Discards any changes and closes the form"
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

